import { Alert, Badge, Button, cardClass, Table, textareaClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { api, type Outcome } from '../api';
import { useApi } from '../hooks';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { outcomeMessage, outcomeTone, Time, useToast } from '../components/ui';

// What an agent learned from its results (ADR 0040): what its work achieved per
// metric, and the lessons drawn from it, each inferred and shown with the
// verdicts it rests on. A person retires a lesson; the outcome engine retires one
// that does worse. Hidden until there is something measured or learned.
// Patterns: data-display/table for results, data-display/list for lessons, content-management/modal to retire one.

interface Group { proposals: number; value: number; sample: number | null; mean: number | null }
interface Lesson {
  id: string; about: string; claim: string; status: string; captured_by: string; created_at: string; retired_reason: string | null; retired_at: string | null;
  evidence: Array<{ knowledge_id: string; metric: string; verdict: string }>; now: Array<{ metric: string; verdict: string }>;
}
interface Outcomes {
  measured: number;
  totals: Array<{ metric: string; title: string; unit: string; better: string } & Group>;
  verdicts: Array<{ knowledge_id: string; metric: string; title: string; verdict: string }>;
  lessons: Lesson[];
}
interface PracticeList { domain: string; sections: Array<{ id: string; title: string }> }

const VERDICT: Record<string, string> = { better: 'did better', worse: 'did worse', not_yet_known: 'not known yet' };
const shown = (t: Outcomes['totals'][number]) => (t.unit === 'rate'
  ? `${t.mean === null ? '–' : `${(t.mean * 100).toFixed(1)}%`} of ${t.sample ?? 0}`
  : `${t.value.toLocaleString()} over ${t.proposals} ${t.proposals === 1 ? 'piece' : 'pieces'} of work`);

export function AgentLearned({ templateId }: { templateId: string }) {
  const out = useApi<Outcomes>(`/v1/outcomes?template_id=${encodeURIComponent(templateId)}`, [templateId]);
  const practice = useApi<PracticeList[]>('/v1/practices', []);
  const [retiring, setRetiring] = useState<Lesson | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const o = out.data;
  if (!o || (!o.measured && !o.lessons.length)) return null;
  const titles = new Map((practice.data ?? []).flatMap((p) => p.sections.map((s) => [s.id, s.title] as const)));
  const about = (id: string) => titles.get(id) ?? (id.startsWith('template:') ? 'how it works overall' : id.startsWith('lesson:') ? 'an earlier lesson' : id);
  const active = o.lessons.filter((l) => l.status === 'active');
  const retired = o.lessons.filter((l) => l.status !== 'active');

  const retire = async () => {
    if (!retiring) return;
    setBusy(true); setError(null);
    try {
      // The key carries the reason: a retry with a corrected reason is a new request, not a conflict.
      const res = await api<Outcome>('/v1/proposals', { body: { schema: 'lesson.retire', payload: { lesson_id: retiring.id.slice('lesson:'.length), reason }, idempotency_key: `retire-lesson:${retiring.id}:${reason}` } });
      if (res.state === 'rejected') { setError(outcomeMessage(res, 'Lesson retired')); return; }
      toast(outcomeMessage(res, 'Lesson retired'), outcomeTone(res));
      setRetiring(null); setReason('');
      void out.reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className={cardClass} aria-labelledby="learned-h">
      <div className="card-head"><h2 id="learned-h">What it learned</h2><span className="faint">{o.measured === 1 ? '1 piece of work measured' : `${o.measured} pieces of work measured`}</span></div>
      {o.totals.length > 0 && (
        <Table label="Its results">
            <caption className="sr-only">What its work achieved</caption>
            <thead><tr><th scope="col">Result</th><th scope="col">So far</th></tr></thead>
            <tbody>{o.totals.map((t) => <tr key={t.metric}><th scope="row">{t.title}</th><td>{shown(t)}</td></tr>)}</tbody>
          </Table>
      )}
      {active.length > 0 && (
        <>
          <h3>Lessons in use</h3>
          <p className="faint small">Each is inferred from this workspace's results, and the agent reads it before it works. Mazu retires one that stops working.</p>
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {active.map((l) => (
              <li key={l.id} className="list-item">
                <div className="grow">
                  <div className="item-title">{l.claim} <Badge tone="info">Inferred</Badge></div>
                  <div className="item-sub">About {about(l.about)}. Work that followed it {l.evidence.map((e) => `${VERDICT[e.verdict] ?? e.verdict} on ${o.verdicts.find((v) => v.metric === e.metric)?.title.toLowerCase() ?? e.metric}`).join(', ')}.</div>
                </div>
                <span className="faint"><Time iso={l.created_at} /></span>
                <Button type="submit" variant="secondary" size="sm" onClick={() => { setRetiring(l); setReason(''); }}>Retire</Button>
              </li>
            ))}
          </ul>
        </>
      )}
      {retired.length > 0 && (
        <details>
          <summary>Retired lessons ({retired.length})</summary>
          <ul className="small">{retired.map((l) => <li key={l.id}>{l.claim}: {l.retired_reason}</li>)}</ul>
        </details>
      )}
      <ConfirmDialog open={!!retiring} title="Retire this lesson?" confirmLabel="Retire lesson" busy={busy} onConfirm={() => void retire()} onCancel={() => { setRetiring(null); setError(null); }}>
        <p style={{ margin: 0 }}>The agent stops reading it from its next run. It stays here with why it ended.</p>
        <Field label="Why" required hint="A few words: at least three letters"><textarea className={textareaClass} rows={2} minLength={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        {error && <Alert tone="danger" role="alert">{error}</Alert>}
      </ConfirmDialog>
    </section>
  );
}
